import { useQuery } from '@tanstack/react-query';
import api from '@/features/api/axios';
import { logger } from '@/features/help/utils/logger';

interface CrmUser {
  id: string;
  name: string;
  email: string;
  active: boolean;
}

interface UseCrmUsersProps {
  enabled?: boolean;
}

/**
 * TanStack Query hook to fetch CRM users/owners for the owner assignment dropdown
 * @param props.enabled - Whether to enable the query (default: true)
 * @returns Object containing users array and loading state
 */
export const useCrmUsers = ({ enabled = true }: UseCrmUsersProps = {}) => {
  const {
    data: users = [],
    isLoading,
    error,
    refetch,
  } = useQuery<CrmUser[]>({
    queryKey: ['crm', 'users'],
    queryFn: async () => {
      logger.debug('useCrmUsers', 'Fetching CRM users');

      const response = await api.get('/api/crm/users');

      logger.debug('useCrmUsers', 'Successfully fetched CRM users', {
        userCount: response.data.users?.length,
      });

      return response.data.users || [];
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - users don't change often
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  return {
    users,
    isLoading,
    error,
    refetch,
  };
};

